import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import YearSelect from 'components/FormFields/YearSelect';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';
import SelectUserModal from '../TrackOpenCredit/components/SelectUserModal';
import { useGetTrackRedeemedCredits } from './gql/get-track-redeemed-credits';
import useRedeemedCreditsExport from './hooks/useRedeemedCreditsExport';

const TrackRedeemedCredits = () => {
  const [credits, setCredits] = useState([]);
  const [count, setCount] = useState(0);
  const [showSelectUserModal, setShowSelectUserModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const { pathname } = useLocation();
  const userType = pathname.split('/').includes('admin-employee')
    ? 'employee'
    : 'track';
  const routePrefix = `/admin-${userType}`;

  const appContext = useContext(AppContext);
  const accountId = appContext?.state?.accountId;

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Year filter - use URL param directly (no local state needed)
  const thisYear = Number(moment().format('YYYY'));
  const yearFilter = input.year ? Number(input.year) : thisYear;

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    updateQuery({ year: newYear, page: 1 });
  };

  // Fetch credits data
  const { resRedeemed, loading } = useGetTrackRedeemedCredits();

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useRedeemedCreditsExport();

  useEffect(() => {
    if (resRedeemed?.getTrackRedeemedCredits) {
      setCredits(resRedeemed.getTrackRedeemedCredits.results || []);
      setCount(resRedeemed.getTrackRedeemedCredits.count || 0);
    }
  }, [resRedeemed]);

  // Handle search - only reset page when user actively searches, not on mount
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle export with selected format
  const handleExport = useCallback(
    async (format) => {
      // Close modal
      setShowExportModal(false);

      // Show processing toaster
      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        // Build filters object
        const filters = {
          ...(input.queryString && { search: input.queryString }),
          ...(yearFilter && { year: yearFilter }),
        };

        // Start export mutation with format
        const jobId = await startExport(accountId, filters, format);

        // Subscribe to progress updates
        subscribeToProgress(jobId, {
          onProgress: (status) => {
            // Update toaster with progress
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} credits (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              // Get download URL
              const downloadUrl = await getDownloadUrl(jobId);

              // Show success toaster with download link
              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } credits exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            // Show error toaster
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        // Show error toaster for mutation failure
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      accountId,
      input.queryString,
      yearFilter,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // DataTable columns
  const columns = [
    {
      field: 'formatted_name',
      header: 'Name',
      render: (item) => <CellPrimary>{item.formatted_name}</CellPrimary>,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  // Actions for DataTable
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      mobileLabel: 'View Details',
      to: (item) =>
        `${routePrefix}/redeemedCredits/creditDetail/${item.id}?year=${yearFilter}`,
    },
  ];

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="credits" />
            <HeaderTitle>Redeemed Credits</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search credits..."
                  />
                  <YearSelect value={yearFilter} onChange={handleYearChange} />
                </PageToolbar>

                <DataTable
                  columns={columns}
                  data={credits}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No redeemed credits found"
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Issue Credit',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                  ),
                  onClick: () => setShowSelectUserModal(true),
                },
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count || 0}
          dataType="redeemed credits"
        />

        {/* Select User Modal */}
        <SelectUserModal
          isOpen={showSelectUserModal}
          onClose={() => setShowSelectUserModal(false)}
        />

        {/* Toaster */}
        {toasterData && (
          <Toaster
            isOpen={!!toasterData}
            onClose={() => setToasterData(null)}
            title={toasterData.title}
            message={toasterData.message}
            type={toasterData.type}
            duration={toasterData.persistent ? 25000 : 10000}
            persistent={toasterData.persistent}
            action={toasterData.action}
          />
        )}
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackRedeemedCredits;
